import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandM,
  BrandS,
  BrandXS,
  BrandXXL,
} from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { MonetaryRewardDisclaimer } from '@sorare/core/src/components/jonum/MonetaryRewardDisclaimer';
import { OnboardingButton } from '@sorare/core/src/components/onboarding/OnboardingButton';
import { ANY_SPORT_ONBOARDING_TUTORIAL_RECRUIT } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MarketGuidanceLayout } from '#src/components/onboarding/MarketGuidanceLayout/index.tsx';
import { estimatedCashPrizeInUserCurrency } from '#src/components/onboarding/cashPrize.tsx';
import { ConversionCredit } from '#src/pages/onboarding_/tutorial/ConversionCredit/index.tsx';

import bezelBg from './assets/bezelBg.webp';
import mlbPlayer from './assets/mlbPlayer.webp';

const Root = styled(Vertical).attrs({ gap: 2 })`
  justify-content: space-between;
  flex: 1;
  width: 100%;

  @media ${tabletAndAbove} {
    width: 100%;
  }
`;

const StyledBezel = styled(Bezel)`
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--double-unit);
  max-width: 500px;
  margin: 0 auto;
`;

const StyledOnboardingButton = styled(OnboardingButton)`
  position: sticky;
  bottom: var(--double-unit);
  align-self: center;
`;

const Bottom = styled(Vertical)`
  align-self: flex-end;
`;

const TutorialIntroPage = () => {
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { sport, generateSportContextPath } = useSportContext();
  const isBaseball = sport === Sport.BASEBALL;

  return (
    <MarketGuidanceLayout progress={1} center ctaBackgroundColor="#0c0524">
      <Root grow>
        <Vertical gap={1.5} center>
          <SorareLogo />
          <BrandM className="text-center">
            <FormattedMessage defaultMessage="You're invited to play Sorare" />
          </BrandM>
        </Vertical>
        <StyledBezel
          background={
            isBaseball
              ? `url(${mlbPlayer}) no-repeat center center / cover`
              : `url(${bezelBg}) no-repeat center center / cover`
          }
        >
          <Bottom center gap={0.5}>
            <BrandXS color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Prize pool" />
            </BrandXS>
            <BrandXXL
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': 'var(--c-gradient-gold)',
                } as CSSProperties
              }
            >
              <MonetaryRewardDisclaimer variant="monetary" withAsterisk={false}>
                <FormattedNumber
                  style="currency"
                  currency={fiatCurrency}
                  value={estimatedCashPrizeInUserCurrency[sport]}
                  currencyDisplay="narrowSymbol"
                  notation="compact"
                  maximumFractionDigits={0}
                />
              </MonetaryRewardDisclaimer>
              *
            </BrandXXL>
            <BrandS>
              <FormattedMessage defaultMessage="each GameWeek" />
            </BrandS>
          </Bottom>
        </StyledBezel>
        <Vertical gap={0}>
          <ConversionCredit />
          <StyledOnboardingButton
            size="large"
            color="secondary"
            to={generateSportContextPath(ANY_SPORT_ONBOARDING_TUTORIAL_RECRUIT)}
            prefetch="render"
          >
            <FormattedMessage {...glossary.continue} />
          </StyledOnboardingButton>
        </Vertical>
      </Root>
    </MarketGuidanceLayout>
  );
};
export default TutorialIntroPage;
